import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useShell } from '../components/PageShell'
import { DollarSign, Tag, Package, TrendingUp } from 'lucide-react'

interface SaleItem {
  id: string
  ic: string
  name: string
  brand: string
  currentQty: number
  thumbnailUrl: string | null
  soldFor: string
  marketplaceFees: string
  shippingFees: string
  quantitySold: string
  marketplace: string
}

interface ItemData {
  id: string
  ic: string
  name: string
  brand: string
  quantity: number
}

const marketplaces = [
  'eBay',
  'Amazon',
  'Mercari',
  'Poshmark',
  'Facebook Marketplace',
  'Shopify',
  'WhatNot',
  'Other'
]

export default function EnterSalePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { toast } = useShell()

  const [items, setItems] = useState<SaleItem[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const itemIds = params.get('items')?.split(',') || []

    if (itemIds.length === 0) {
      toast({ title: 'No Items', description: 'No items selected for sale', type: 'error' })
      navigate('/search')
      return
    }

    fetchItems(itemIds)
  }, [location.search])

  const fetchItems = async (itemIds: string[]) => {
    try {
      // Fetch all items using search-items endpoint with large limit
      const response = await fetch(`/api/search-items?limit=1000`)
      const result = await response.json()

      if (!result.success || !result.data?.items) {
        toast({ title: 'Error', description: 'Failed to load items', type: 'error' })
        navigate('/search')
        return
      }

      // Filter to only the items we need
      const filteredItems = result.data.items.filter((item: any) => itemIds.includes(item.id))

      const saleItems: SaleItem[] = filteredItems.map((item: any) => ({
        id: item.id,
        ic: item.ic,
        name: item.title || item.name,
        brand: item.brand || '',
        currentQty: item.quantity || 0,
        thumbnailUrl: item.thumbnail_url || item.primary_image_url || null,
        soldFor: '',
        marketplaceFees: '',
        shippingFees: '',
        quantitySold: '1',
        marketplace: 'eBay'
      }))

      if (saleItems.length === 0) {
        toast({ title: 'Error', description: 'Failed to load items', type: 'error' })
        navigate('/search')
        return
      }

      setItems(saleItems)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching items:', error)
      toast({ title: 'Error', description: 'Failed to load items', type: 'error' })
      navigate('/search')
    }
  }

  const updateItem = (index: number, field: keyof SaleItem, value: string) => {
    const updated = [...items]
    updated[index] = { ...updated[index], [field]: value }
    setItems(updated)
  }

  const handleSubmit = async () => {
    // Validate all items
    for (const item of items) {
      if (!item.soldFor || parseFloat(item.soldFor) <= 0) {
        toast({ title: 'Invalid Input', description: `${item.ic}: Sold For is required`, type: 'error' })
        return
      }

      if (!item.marketplace) {
        toast({ title: 'Invalid Input', description: `${item.ic}: Marketplace is required`, type: 'error' })
        return
      }

      const qty = parseInt(item.quantitySold)
      if (!qty || qty <= 0 || qty > item.currentQty) {
        toast({ title: 'Invalid Quantity', description: `${item.ic}: Quantity must be between 1 and ${item.currentQty}`, type: 'error' })
        return
      }
    }

    setSubmitting(true)

    try {
      const sales = items.map(item => ({
        item_id: item.id,
        sold_for: parseFloat(item.soldFor),
        marketplace_fees: parseFloat(item.marketplaceFees) || 0,
        shipping_fees: parseFloat(item.shippingFees) || 0,
        quantity: parseInt(item.quantitySold),
        marketplace: item.marketplace
      }))

      const response = await fetch('/api/record-sale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sales })
      })

      const data = await response.json()

      if (data.success) {
        let description = `${data.data.sales_count} sale(s) recorded`
        if (data.data.pick_list_id) {
          description += `, ${data.data.pick_list_items_count} item(s) added to pick list`
        }

        toast({ title: 'Sales Recorded', description, type: 'success' })
        navigate('/search')
      } else {
        toast({ title: 'Error', description: data.error || 'Failed to record sales', type: 'error' })
      }
    } catch (error) {
      console.error('Error recording sales:', error)
      toast({ title: 'Error', description: 'Failed to record sales', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const calculateNetProfit = (item: SaleItem) => {
    const soldFor = parseFloat(item.soldFor) || 0
    const marketplaceFees = parseFloat(item.marketplaceFees) || 0
    const shippingFees = parseFloat(item.shippingFees) || 0
    const qty = parseInt(item.quantitySold) || 1

    return ((soldFor - marketplaceFees - shippingFees) * qty).toFixed(2)
  }

  const calculateTotalRevenue = () => {
    return items.reduce((sum, item) => {
      const soldFor = parseFloat(item.soldFor) || 0
      const qty = parseInt(item.quantitySold) || 1
      return sum + (soldFor * qty)
    }, 0).toFixed(2)
  }

  const calculateTotalProfit = () => {
    return items.reduce((sum, item) => {
      return sum + parseFloat(calculateNetProfit(item))
    }, 0).toFixed(2)
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        color: '#E0E6F0',
        fontFamily: 'Rajdhani, sans-serif',
        fontSize: '1.25rem'
      }}>
        Loading items...
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif',
      color: '#E0E6F0'
    }}>
      {/* Page Title */}
      <h1 style={{
        fontSize: '48px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '8px',
        background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '0 0 40px rgba(251, 146, 60, 0.5)',
        filter: 'drop-shadow(0 0 20px rgba(251, 146, 60, 0.3))'
      }}>
        Enter Sale Details
      </h1>
      <p style={{
        fontSize: '14px',
        color: '#9CA3AF',
        marginBottom: '32px',
        letterSpacing: '0.025em'
      }}>
        Record sale information for {items.length} item(s)
      </p>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '32px'
      }}>
        <div style={{
          position: 'relative',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(6, 182, 212, 0.2) 50%, rgba(17, 24, 39, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(6, 182, 212, 0.2)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '128px',
            height: '128px',
            background: 'linear-gradient(to bottom right, transparent, transparent, rgba(6, 182, 212, 0.1))',
            pointerEvents: 'none'
          }} />
          <div style={{
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)'
          }}>
            <Package style={{ width: 28, height: 28, color: '#22D3EE' }} />
          </div>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '4px'
            }}>Items</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF' }}>{items.length}</div>
          </div>
        </div>

        <div style={{
          position: 'relative',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(251, 146, 60, 0.2) 50%, rgba(17, 24, 39, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(251, 146, 60, 0.2)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '128px',
            height: '128px',
            background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))',
            pointerEvents: 'none'
          }} />
          <div style={{
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(251, 146, 60, 0.1)',
            border: '1px solid rgba(251, 146, 60, 0.3)'
          }}>
            <DollarSign style={{ width: 28, height: 28, color: '#FB923C' }} />
          </div>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '4px'
            }}>Total Revenue</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF' }}>${calculateTotalRevenue()}</div>
          </div>
        </div>

        <div style={{
          position: 'relative',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(16, 185, 129, 0.2) 50%, rgba(17, 24, 39, 0.95) 100%)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '128px',
            height: '128px',
            background: 'linear-gradient(to bottom right, transparent, transparent, rgba(16, 185, 129, 0.1))',
            pointerEvents: 'none'
          }} />
          <div style={{
            padding: '12px',
            borderRadius: '8px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}>
            <TrendingUp style={{ width: 28, height: 28, color: '#34D399' }} />
          </div>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#9CA3AF',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '4px'
            }}>Net Profit</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF' }}>${calculateTotalProfit()}</div>
          </div>
        </div>
      </div>

      {/* Item Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {items.map((item, index) => (
          <div key={item.id} style={{
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.95) 0%, rgba(251, 146, 60, 0.2) 50%, rgba(17, 24, 39, 0.95) 100%)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(251, 146, 60, 0.2)',
            borderRadius: '12px',
            padding: '24px',
            transition: 'all 0.3s ease',
            overflow: 'hidden'
          }}>
            {/* Corner accent pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '128px',
              height: '128px',
              background: 'linear-gradient(to bottom right, transparent, transparent, rgba(251, 146, 60, 0.1))',
              pointerEvents: 'none'
            }} />

            {/* Item Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(251, 146, 60, 0.2)'
            }}>
              {/* Thumbnail */}
              {item.thumbnailUrl ? (
                <img
                  src={item.thumbnailUrl}
                  alt={item.name}
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid rgba(251, 146, 60, 0.3)'
                  }}
                />
              ) : (
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: 'rgba(17, 24, 39, 0.8)',
                  border: '1px solid rgba(251, 146, 60, 0.3)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package style={{ width: 32, height: 32, color: 'rgba(251, 146, 60, 0.5)' }} />
                </div>
              )}

              {/* Item Info */}
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  <Tag style={{ width: 18, height: 18, color: '#22D3EE' }} />
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#FB923C',
                    letterSpacing: '0.025em'
                  }}>
                    {item.ic}
                  </div>
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  marginBottom: '6px'
                }}>
                  {item.name}
                </div>
                {item.brand && (
                  <div style={{
                    fontSize: '14px',
                    color: '#9CA3AF'
                  }}>
                    {item.brand}
                  </div>
                )}
              </div>

              {/* Quantity Badge */}
              <div style={{
                padding: '8px 16px',
                background: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid rgba(6, 182, 212, 0.3)',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#22D3EE',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Qty Available: {item.currentQty}
              </div>
            </div>

            {/* Form Fields */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {/* Sold For */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FB923C',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Sold For * <span style={{ color: '#9CA3AF', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>(per unit)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.soldFor}
                  onChange={(e) => updateItem(index, 'soldFor', e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(251, 146, 60, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.5)'
                    e.target.style.boxShadow = '0 0 0 3px rgba(251, 146, 60, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.3)'
                    e.target.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Marketplace Fees */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#22D3EE',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Marketplace Fees <span style={{ color: '#9CA3AF', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>(total)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.marketplaceFees}
                  onChange={(e) => updateItem(index, 'marketplaceFees', e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)'
                    e.target.style.boxShadow = '0 0 0 3px rgba(6, 182, 212, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.3)'
                    e.target.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Shipping Fees */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#22D3EE',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Shipping Fees <span style={{ color: '#9CA3AF', fontWeight: 400, textTransform: 'none', letterSpacing: 'normal' }}>(total)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.shippingFees}
                  onChange={(e) => updateItem(index, 'shippingFees', e.target.value)}
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)'
                    e.target.style.boxShadow = '0 0 0 3px rgba(6, 182, 212, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.3)'
                    e.target.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Quantity Sold */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FB923C',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Quantity Sold *
                </label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  max={item.currentQty}
                  value={item.quantitySold}
                  onChange={(e) => updateItem(index, 'quantitySold', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(251, 146, 60, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.5)'
                    e.target.style.boxShadow = '0 0 0 3px rgba(251, 146, 60, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.3)'
                    e.target.style.boxShadow = 'none'
                  }}
                />
              </div>

              {/* Marketplace */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FB923C',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Marketplace *
                </label>
                <select
                  value={item.marketplace}
                  onChange={(e) => updateItem(index, 'marketplace', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(251, 146, 60, 0.3)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '16px',
                    fontFamily: 'Rajdhani, sans-serif',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.5)'
                    e.target.style.boxShadow = '0 0 0 3px rgba(251, 146, 60, 0.1)'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(251, 146, 60, 0.3)'
                    e.target.style.boxShadow = 'none'
                  }}
                >
                  {marketplaces.map(mp => (
                    <option key={mp} value={mp} style={{ background: '#1A1F2E', color: '#FFFFFF' }}>{mp}</option>
                  ))}
                </select>
              </div>

              {/* Net Profit Display */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#34D399',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Net Profit
                </label>
                <div style={{
                  padding: '12px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#34D399',
                  letterSpacing: '0.025em'
                }}>
                  ${calculateNetProfit(item)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div style={{
        marginTop: '32px',
        display: 'flex',
        gap: '16px',
        justifyContent: 'flex-end',
        paddingTop: '24px',
        borderTop: '1px solid rgba(251, 146, 60, 0.2)'
      }}>
        <button
          onClick={() => navigate('/search')}
          disabled={submitting}
          style={{
            padding: '16px 32px',
            fontFamily: 'Rajdhani, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: 'rgba(17, 24, 39, 0.8)',
            border: '1px solid rgba(156, 163, 175, 0.3)',
            color: '#FFFFFF',
            borderRadius: '8px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.5 : 1,
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            if (!submitting) {
              e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.5)'
              e.currentTarget.style.background = 'rgba(42, 49, 66, 0.8)'
              e.currentTarget.style.transform = 'translateY(-2px)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(156, 163, 175, 0.3)'
            e.currentTarget.style.background = 'rgba(17, 24, 39, 0.8)'
            e.currentTarget.style.transform = 'translateY(0)'
          }}
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            padding: '16px 32px',
            fontFamily: 'Rajdhani, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)',
            border: '1px solid rgba(251, 146, 60, 0.5)',
            color: '#FFFFFF',
            borderRadius: '8px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.5 : 1,
            transition: 'all 0.3s ease',
            boxShadow: '0 0 30px rgba(251, 146, 60, 0.3)'
          }}
          onMouseEnter={(e) => {
            if (!submitting) {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 0 40px rgba(251, 146, 60, 0.5)'
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 0 30px rgba(251, 146, 60, 0.3)'
          }}
        >
          {submitting ? 'Recording Sales...' : 'Record Sales'}
        </button>
      </div>
    </div>
  )
}
